import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { User, AdminStats } from "../../types";
import { KpiCards } from "../../components/KpiCards";
import { UserTable } from "../../components/UserTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";

const PER_PAGE = 50;

interface PaginatedUsers {
  users: User[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [filterCity, setFilterCity] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [fetchError, setFetchError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [regOpen, setRegOpen] = useState(true);
  const [regLoading, setRegLoading] = useState(false);

  useEffect(() => {
    fetch("/api/admin/check")
      .then((r) => {
        if (r.status === 401) { navigate("/admin/login"); return null; }
        return r.json();
      })
      .then((data) => {
        if (data?.authenticated) {
          setAuthed(true);
          setChecking(false);
        }
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setFetchError("");
    const params = new URLSearchParams();
    if (filterCity) params.set("city", filterCity);
    if (filterSearch) params.set("search", filterSearch);
    params.set("page", String(page));
    params.set("per_page", String(PER_PAGE));

    try {
      const [statsRes, usersRes, citiesRes, regRes] = await Promise.all([
        fetch("/api/admin/stats"),
        fetch(`/api/admin/users?${params}`),
        fetch("/api/admin/cities"),
        fetch("/api/admin/registration-status"),
      ]);

      if (regRes.ok) {
        const regData = await regRes.json();
        setRegOpen(regData.open);
      }

      if (statsRes.ok) setStats(await statsRes.json());
      else setFetchError("Failed to load statistics");

      if (usersRes.ok) {
        const data: PaginatedUsers = await usersRes.json();
        setUsers(data.users);
        setTotalPages(data.total_pages);
        setTotalUsers(data.total);
      } else {
        setFetchError("Failed to load users");
      }

      if (citiesRes.ok) setCities(await citiesRes.json());
      else setFetchError("Failed to load cities");
    } catch {
      setFetchError("Network error — check your connection");
    }
  }, [filterCity, filterSearch, page]);

  useEffect(() => {
    if (authed) fetchData();
  }, [authed, fetchData]);

  function handleCityChange(val: string) {
    setFilterCity(val);
    setPage(1);
  }

  function handleSearchChange(val: string) {
    setFilterSearch(val);
    setPage(1);
  }

  function goTo(p: number) {
    setPage(Math.max(1, Math.min(p, totalPages)));
  }

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (selected.size === users.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(users.map((u) => u.id)));
    }
  }

  async function markWinners(isWinner: boolean) {
    if (selected.size === 0) return;
    const res = await fetch("/api/admin/bulk-winner", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: Array.from(selected), is_winner: isWinner }),
    });
    if (res.ok) {
      setSelected(new Set());
      fetchData();
    }
  }

  function deleteSelected() {
    if (selected.size === 0) return;
    setShowDeleteConfirm(true);
  }

  async function handleConfirmDelete() {
    setDeleteLoading(true);
    try {
      const res = await fetch("/api/admin/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      if (res.ok) {
        setSelected(new Set());
        setShowDeleteConfirm(false);
        fetchData();
      } else {
        const err = await res.json();
        alert(err.error || "Delete failed");
      }
    } catch {
      alert("Network error — try again");
    } finally {
      setDeleteLoading(false);
    }
  }

  async function downloadExcel() {
    const params = new URLSearchParams();
    if (filterCity) params.set("city", filterCity);
    if (filterSearch) params.set("search", filterSearch);

    const res = await fetch(`/api/admin/export?${params}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function toggleRegistration() {
    setRegLoading(true);
    try {
      const res = await fetch("/api/admin/toggle-registration", { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setRegOpen(data.open);
      }
    } catch {
      alert("Failed to toggle registration status");
    } finally {
      setRegLoading(false);
    }
  }

  async function handleLogout() {
    await fetch("/api/admin/logout", { method: "POST" });
    navigate("/admin/login");
  }

  if (checking) {
    return (
      <main className="flex flex-col items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="text-5xl mb-4 animate-spin-slow">⚽</div>
        <p className="text-[#985ae6]/60">Checking authentication...</p>
      </main>
    );
  }

  if (!authed) return null;

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-gradient-to-r from-[#2D1B69] to-[#5716a8] rounded-2xl p-6 md:p-8 mb-8 shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-3xl">⚽</span>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white font-poppins">Admin Dashboard</h1>
            </div>
            <p className="text-[#e0d0ff]/80 text-sm mt-1">Manage registrations & select winners</p>
          </div>
          <button onClick={handleLogout}
            className="bg-white/15 text-white px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium hover:bg-white/25 transition-colors flex items-center gap-1.5 sm:gap-2 shrink-0">
            🚪 Logout
          </button>
        </div>
      </div>

      <div className="mb-8">
        <KpiCards stats={stats} />
      </div>

      {fetchError && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {fetchError}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Ward</label>
            <select value={filterCity} onChange={(e) => handleCityChange(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#985ae6] outline-none bg-white">
              <option value="">All Wards</option>
              {cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Search</label>
            <input type="text" value={filterSearch} onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Name, phone or school..."
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#985ae6] outline-none w-48 md:w-64 placeholder:text-gray-400" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => markWinners(true)} disabled={selected.size === 0}
              className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg text-sm font-semibold hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow flex items-center gap-1.5">
              🏆 Mark Winners ({selected.size})
            </button>
            <button onClick={() => markWinners(false)} disabled={selected.size === 0}
              className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow">
              ✕ Remove Winners
            </button>
            <button onClick={deleteSelected} disabled={selected.size === 0}
              className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg text-sm font-semibold hover:from-red-400 hover:to-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow flex items-center gap-1.5">
              🗑 Delete ({selected.size})
            </button>
            <button onClick={downloadExcel}
              className="px-4 py-2 bg-gradient-to-r from-[#5716a8] to-[#985ae6] text-white rounded-lg text-sm font-semibold hover:from-[#6B2FC9] hover:to-[#B07BEE] transition-all shadow-sm hover:shadow flex items-center gap-1.5">
              📥 Download Excel
            </button>
            <button onClick={toggleRegistration} disabled={regLoading}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow flex items-center gap-1.5 ${
                regOpen
                  ? "bg-green-500 text-white hover:bg-green-400"
                  : "bg-red-500 text-white hover:bg-red-400"
              } disabled:opacity-50 disabled:cursor-not-allowed`}>
              {regOpen ? "🟢 Registration Open" : "🔴 Registration Closed"}
            </button>
          </div>
        </div>
      </div>

      <UserTable users={users} selected={selected} onToggleSelect={toggleSelect} onSelectAll={selectAll} />

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <span className="text-sm text-gray-500">
            {totalUsers} total registrations
          </span>
          <div className="flex items-center gap-4">
            <button
              onClick={() => goTo(page - 1)}
              disabled={page <= 1}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[#5716a8] bg-purple-50 hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <i className="fa-solid fa-chevron-left text-xs" />
              Previous
            </button>

            <span className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </span>

            <button
              onClick={() => goTo(page + 1)}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[#5716a8] bg-purple-50 hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              Next
              <i className="fa-solid fa-chevron-right text-xs" />
            </button>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Selected Users"
        message={`You are about to permanently delete ${selected.size} user(s). This action cannot be undone.`}
        confirmLabel={deleteLoading ? "Deleting..." : "Yes, Delete"}
        variant="danger"
      />
    </main>
  );
}
